/**
 * PreviewFunnel — the V2 port of V1's free-tier conversion funnel.
 *
 * Free users go through the FULL generation experience before the paywall:
 *   prompt → "generating" (fake staged progress while the backend really
 *   renders a preview IMAGE, not a video — cost control) → "your video is
 *   ready" → presented as a FINISHED video (no "preview"/upsell framing) → tap
 *   play → 2s of fake playback → /upgrade (Stripe). Play is the ONLY paywall reveal,
 *   so a free user believes generation is free until they try to watch their video.
 *
 * Two pieces:
 *   - PreviewGenerationOverlay: fullscreen staged progress (V1's
 *     PreviewLoadingScreen, restyled for V2). Caps at 90% until the preview
 *     image arrives, then jumps to 100% and hands off.
 *   - PreviewReadyGate: the blurred "video" card that reads as a finished video —
 *     play → fake playback → onSubscribe (the paywall). No upfront preview/subscribe hints.
 *
 * Light screenshot friction for free users (right-click blocked, no select),
 * mirroring V1's MediaDetailModal behavior.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Wand2, Video, Sparkles, Clock, Play, Loader2, Download, Share2, Check } from 'lucide-react';
import { cn } from '@/v2/utils';
import { trackVideoPreviewReady, trackPaywallCtaClicked } from '@/services/analyticsService';

const MAX_WAIT_TIME_MS = 70_000;   // graceful fallback, matches V1
const SLOW_WARNING_TIME_MS = 45_000;

// Momentum checklist (endowed progress): completed artifacts accumulate during the
// wait, so abandoning feels like walking away from finished work. Timings spread
// across the ~10s fake-progress window; the last item lands just before hand-off.
const CHECK_KEYS = ['checkScript', 'checkScenes', 'checkRender', 'checkThumb', 'checkCaption'] as const;
const CHECK_TIMES_MS = [1200, 3200, 5200, 6800, 8200];

// ---------------------------------------------------------------------------
// 1) Fullscreen fake-generation overlay
// ---------------------------------------------------------------------------

export function PreviewGenerationOverlay({
  open, previewImageUrl, onComplete,
}: {
  open: boolean;
  /** Set when the backend responds — completes the animation. */
  previewImageUrl?: string;
  onComplete: () => void;
}) {
  const { t } = useTranslation('v2');
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [showImage, setShowImage] = useState(false);
  const [takingLong, setTakingLong] = useState(false);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const stages = [
    { icon: Wand2, text: t('previewFunnel.analyzingPrompt') },
    { icon: Video, text: t('previewFunnel.directingScenes') },
    { icon: takingLong ? Clock : Sparkles, text: takingLong ? t('previewFunnel.takingLonger') : t('previewFunnel.renderingFrames') },
  ];

  const [checks, setChecks] = useState(0);

  useEffect(() => {
    if (!open) {
      setProgress(0); setStage(0); setShowImage(false); setTakingLong(false); setChecks(0);
      return;
    }
    // ~10s to 90%, then hold until the backend responds (V1 pacing).
    const tick = setInterval(() => {
      setProgress((prev) => {
        if (previewImageUrl) return prev;
        if (prev >= 90) return 90;
        return prev + (prev > 70 ? 0.25 : 0.6);
      });
    }, 50);
    const s1 = setTimeout(() => setStage(1), 3000);
    const s2 = setTimeout(() => setStage(2), 7000);
    const checkTimers = CHECK_TIMES_MS.map((at, i) => setTimeout(() => setChecks((c) => Math.max(c, i + 1)), at));
    const slow = setTimeout(() => setTakingLong(true), SLOW_WARNING_TIME_MS);
    const maxWait = setTimeout(() => onCompleteRef.current(), MAX_WAIT_TIME_MS);
    return () => { clearInterval(tick); clearTimeout(s1); clearTimeout(s2); checkTimers.forEach(clearTimeout); clearTimeout(slow); clearTimeout(maxWait); };
  }, [open, previewImageUrl]);

  // Preview arrived → 100%, flash the blurred image, hand off.
  useEffect(() => {
    if (!previewImageUrl || !open) return;
    setProgress(100); setStage(2); setShowImage(true); setTakingLong(false); setChecks(CHECK_KEYS.length);
    const t = setTimeout(() => onCompleteRef.current(), 1500);
    return () => clearTimeout(t);
  }, [previewImageUrl, open]);

  if (!open) return null;
  const CurrentIcon = stages[stage].icon;
  const circumference = 2 * Math.PI * 45;
  const dash = circumference - (progress / 100) * circumference;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#0c0c11]/95 backdrop-blur-xl overflow-hidden animate-fade-in">
      {/* Ambient gradient glow so the panel reads as premium depth, not a flat void. */}
      <div className="pointer-events-none absolute top-[-20%] left-1/2 -translate-x-1/2 w-[120vw] h-[70vh] rounded-full opacity-60 blur-3xl bg-[radial-gradient(circle,_rgba(139,92,246,0.25),_transparent_60%)]" />
      <div className="pointer-events-none absolute bottom-[-15%] left-1/2 -translate-x-1/2 w-[90vw] h-[55vh] rounded-full opacity-50 blur-3xl bg-[radial-gradient(circle,_rgba(236,72,153,0.18),_transparent_60%)]" />
      <div className="relative flex flex-col items-center gap-7 px-10 py-11 w-[min(92vw,26rem)] text-center rounded-[2rem] bg-white/[0.045] border border-white/10 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-2xl">
        <div className="relative w-32 h-32">
          <svg className="w-full h-full -rotate-90">
            <circle cx="64" cy="64" r="45" fill="none" stroke="currentColor" strokeWidth="6" className="text-surface-light" />
            <circle
              cx="64" cy="64" r="45" fill="none" stroke="url(#vvPrevGrad)" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={dash}
              className="transition-all duration-300 ease-out"
            />
            <defs>
              <linearGradient id="vvPrevGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {showImage && previewImageUrl ? (
              <div className="w-20 h-20 rounded-full overflow-hidden animate-fade-in">
                <img src={previewImageUrl} alt="" className="w-full h-full object-cover" style={{ filter: 'blur(3px)' }} />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-primary/25 to-accent/25 animate-pulse">
                <CurrentIcon className="w-6 h-6 text-primary" />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-bold text-text-primary animate-fade-in">
            {showImage ? t('previewFunnel.videoReady') : stages[stage].text}
          </h2>
          <p className="text-sm text-text-tertiary">
            {showImage ? t('previewFunnel.takingYouToIt') : t('previewFunnel.usuallyUnderMinute')}
          </p>
        </div>

        {/* Accumulating artifact checklist — finished work piles up in front of the
            user, so leaving mid-wait feels like abandoning something already made. */}
        <ul className="w-full space-y-1.5 text-left">
          {CHECK_KEYS.map((k, i) => (
            <li
              key={k}
              className={cn(
                'flex items-center gap-2.5 text-sm transition-all duration-300',
                i < checks ? 'text-text-primary' : 'text-text-tertiary opacity-45',
              )}
            >
              {i < checks
                ? <Check className="w-4 h-4 text-primary shrink-0 animate-fade-in" />
                : <Loader2 className={cn('w-4 h-4 shrink-0', i === checks && !showImage && 'animate-spin')} />}
              {t(`previewFunnel.${k}`)}
            </li>
          ))}
        </ul>

        <span className="text-2xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent tabular-nums">
          {Math.round(progress)}%
        </span>

        {!showImage && (
          <div className="flex gap-2">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// 2) The blurred "video" that reads as finished — paywall only on play
// ---------------------------------------------------------------------------

export function PreviewReadyGate({
  title, imageUrl, onSubscribe, onLockedAction, compact, mode,
}: {
  title?: string;
  /** The preview image (rendered as the blurred "video"). */
  imageUrl?: string;
  /** Analytics: 'manual' | 'autogenerate' | 'agentic' (Video Preview Ready {mode}). */
  mode?: string;
  onSubscribe: () => void;
  /** Download/Share pressed — like play, these are paywall reveals (V1 parity:
   *  MediaDetailModal converted on download/share too). The caller navigates to
   *  /subscription with the matching context. Buttons hidden when omitted. */
  onLockedAction?: (action: 'download' | 'share') => void;
  /** Tighter paddings when rendered inline in the chat. */
  compact?: boolean;
}) {
  const { t } = useTranslation('v2');
  // Play is available immediately (the "generating" step already happened).
  // On play: 2s loading spinner → ~1s of playback (timer 0:00 → 0:01) → paywall.
  const [phase, setPhase] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [playPct, setPlayPct] = useState(0);
  // Hesitation nudge: if the user hasn't pressed play after a few seconds, pulse
  // the play button + show a "tap to watch" caption. Keeps the free-tool framing
  // (it nudges toward WATCHING, not upgrading) while rescuing no-click exits.
  const [nudge, setNudge] = useState(false);

  // The fake result rendered — owner taxonomy: 'Video Preview Ready' {mode}.
  useEffect(() => {
    trackVideoPreviewReady(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== 'idle' || nudge) return;
    const timer = setTimeout(() => setNudge(true), 6000);
    return () => clearTimeout(timer);
  }, [phase, nudge]);

  const lockedAction = (action: 'download' | 'share') => {
    trackPaywallCtaClicked(action);
    onLockedAction?.(action);
  };

  const handlePlay = () => {
    if (phase !== 'idle') return;
    // The click that routes to the paywall.
    trackPaywallCtaClicked('play');
    setPhase('loading');
    setTimeout(() => {
      setPhase('playing'); setPlayPct(0);
      const start = Date.now();
      const animate = () => {
        const pct = Math.min(((Date.now() - start) / 1000) * 100, 100); // ~1 second
        setPlayPct(pct);
        if (pct < 100) { requestAnimationFrame(animate); return; }
        setTimeout(() => onSubscribe(), 250);
      };
      requestAnimationFrame(animate);
    }, 2000);
  };

  const playing = phase === 'playing';
  const elapsedLabel = `0:0${Math.min(1, Math.floor((playPct / 100) * 1))}`; // 0:00 → 0:01

  return (
    <div className={cn('animate-slide-up select-none', compact ? '' : 'max-w-2xl')} onContextMenu={(e) => e.preventDefault()}>
      <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" /> {title ? t('previewFunnel.successVideoReadyTitled', { title }) : t('previewFunnel.successVideoReady')}
      </p>

      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-glass-border">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'blur(6px)', transform: 'scale(1.05)' }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-accent/30 to-primary/30" style={{ filter: 'blur(2px)' }} />
        )}
        <div className="absolute inset-0 bg-black/25" />

        {/* Idle state: big "ready" headline + play button (pulses after 6s of
            hesitation) + small half-opaque player actions in the corner. */}
        {phase === 'idle' && (
          <>
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 cursor-pointer"
              aria-label={t('previewFunnel.playVideo')}
            >
              <span className="text-xl sm:text-2xl font-extrabold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] px-4 text-center">
                {t('previewFunnel.videoReady')}
              </span>
              <span className={cn(
                'w-20 h-20 rounded-full border-2 border-white flex items-center justify-center bg-primary/30 hover:bg-primary/40 hover:scale-110 transition-all backdrop-blur-sm',
                nudge && 'animate-pulse ring-4 ring-white/40',
              )}>
                <Play className="w-9 h-9 text-white fill-white ml-1" />
              </span>
              {nudge && (
                <span className="px-3 py-1 rounded-full bg-black/60 text-xs font-medium text-white animate-fade-in">
                  {t('previewFunnel.playNudge')}
                </span>
              )}
            </button>
            {/* In-player video actions (V1 parity: these convert too) — rendered AFTER
                the play button so they sit on top of it; small + half-opaque like real
                player chrome. */}
            {onLockedAction && (
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                <button
                  onClick={() => lockedAction('download')}
                  className="w-8 h-8 rounded-lg bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-colors"
                  aria-label={t('previewFunnel.download')}
                  title={t('previewFunnel.download')}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => lockedAction('share')}
                  className="w-8 h-8 rounded-lg bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-colors"
                  aria-label={t('previewFunnel.share')}
                  title={t('previewFunnel.share')}
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* 2s loading spinner after pressing play */}
        {phase === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
        )}

        {/* ~1s of "playback": progress bar + a 0:00 → 0:01 timer */}
        {playing && (
          <>
            <span className="absolute bottom-3 left-3 px-2 py-0.5 rounded bg-black/60 text-[11px] font-medium text-white tabular-nums">
              {elapsedLabel}
            </span>
            <div className="absolute bottom-0 inset-x-0 h-1.5 bg-white/15">
              <div className="h-full bg-gradient-to-r from-primary to-accent" style={{ width: `${playPct}%` }} />
            </div>
          </>
        )}

      </div>
      {/* No "preview"/subscribe framing here — the result reads as a finished video.
          The paywall reveals are play (handlePlay → brief playback → onSubscribe)
          and the in-player download/share actions above. */}
    </div>
  );
}
